//Componentes generales
import React, { useState, useEffect } from "react";
import moment from "moment";
import { getConceptosByTDoc } from "../../api/concepto";

// Iconos
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faBox,
  faRotateRight,
  faFilePdf,
  faDownload,
  faPen,
  faRectangleXmark,
  faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons";

//Estilos
import "./ReqInfo.css";
import {
  EDITA_Y_VISUALIZA_NOTAS_PRIVADAS_COMPROBANTE,
  MODIFICA_GRUPOS_COMPROBANTES,
} from "../../utils/permisos";

/* 
    Componente encargado de mostrar el detalle del comprobante.
        Donde item es el comprobante seleccionado.
              files son los archivos adjuntados al comprobante.
              cerrar es la función encargada de ocultar el dialogo.
              verDetalle es la función del context que recupera los productos del comprobante.
              repetir es una función que llama al componente que corresponda.
              generaPDF es una función que llama al componente que corresponda.
              abrirURL es la función encargada de abrir en una nueva ventana el archivo seleccionado.
              guardarModificaciones es la función del context que guarda el comprobante en el caso de haber modificaciones.
              usuario es el usuario en sesión.

*/
function ReqInfo({
  item = {},
  files,
  cerrar,
  verDetalle,
  repetir,
  generaPDF,
  abrirURL,
  guardarModificaciones,
  usuario,
}) {
  //state de productos.
  const [products, setProducts] = useState([]);
  //state de conceptos.
  const [conceptos, setConceptos] = useState([]);
  //state de comprobante.
  const [comprobante, setComprobante] = useState(item);
  //state para edición de nota.
  const [editNota, setEditNota] = useState(false);
  //state para edición de nota privada.
  const [editNotaReservada, setEditNotaReservada] = useState(false);

  /*
        Al renderizar el componente, recupera los productos del comprobante seleccionado.
    */
  useEffect(() => {
    async function fp() {
      try {
        const p = await verDetalle(item.tDocCodFor, item.tDocNroFor);
        setProducts(p);
        const concResp = await getConceptosByTDoc(item.tDocCodFor, item.tDocNroFor);
        setConceptos(concResp?.data?.data || []);
      } catch (error) {
        console.log("error");
      }
    }
    fp();
  }, []);

  /*
        Disparador que se activa al detectar algun cambio en el item. Setea el comprobante con el item que fue modificado.
    */
  useEffect(() => {
    setComprobante(item);
  }, [item]);

  /*
        Manejador que descargar/abre todos los archivos adjuntados al comprobante. 
    */
  const handleDownloadAll = () => {
    files.map((file) => abrirURL(file.url));
  };

  /* 
        Manejador que determina si habilitar o no la edición del campo Nota.
    */
  const handleEditNota = () => {
    setEditNota(!editNota);
  };

  /* 
        Manejador que determina si habilitar o no la edición del campo Nota Reservada.
    */
  const handleEditNotaReservada = () => {
    setEditNotaReservada(!editNotaReservada);
  };

  /* 
        Manejador ante cambio en casilla de Nota.
            Donde valor es lo ingresado por el usuario.
    */
  const handleModificaNota = (valor) => {
    setComprobante({ ...comprobante, notePubl: valor });
  };

  /* 
        Manejador ante cambio en casilla de Nota Reservada.
            Donde valor es lo ingresado por el usuario.
    */
  const handleModificaNotaReservada = (valor) => {
    setComprobante({ ...comprobante, notePriv: valor });
  };

  const saveChangesNota = async (valor) => {
    setEditNota(false);

    const obj = { notePubl: comprobante.notePubl };
    const resp = await guardarModificaciones(comprobante, obj);
    setComprobante(resp.data);
  };

  /* 
        Guarda el comprobante mediante función del context.
    */
  const saveChangesNotaReservada = async (valor) => {
    setEditNotaReservada(false);

    const obj = { notePriv: comprobante.notePriv };
    const resp = await guardarModificaciones(comprobante, obj);
    setComprobante(resp.data);
  };

  // Formatter para miles y decimales
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Crear importe formateado
  const importeTotalString = formatter.format(
    comprobante.importe ? comprobante.importe : 0
  );

  // Crear cotizacion formateada
  const cotizacionTotalString = formatter.format(
    comprobante.cotizacion ? comprobante.cotizacion : 0
  );

  return (
    <div className="container-gr-req">
      <div className="container-r-list-item-header-info">
        <div className="label-req">
          {comprobante.tDocCodFor + " - " + comprobante.tDocNroFor}
        </div>
        <div className="container-codOri-observada">
          <div className="r-list-item-header-title">{comprobante.codOri}</div>
          {comprobante.observada && (
            <div className="r-list-item-details-observada">OBSERVADA</div>
          )}
        </div>
        <FontAwesomeIcon
          style={{ cursor: "pointer" }}
          icon={faXmark}
          onClick={() => cerrar(null)}
        />
      </div>
      <div className="container-r-item-details">
        <div className="container-r-info-items">
          <div className="container-r-info-p">
            <div className="label-req-title">Estado Actual </div>
            <div className="r-list-item-details-title-v">
              {comprobante.state}
            </div>
            <div className="label-req-title">Emitido por </div>
            <div className="r-list-item-details-title-v">
              {comprobante.user[0].fullName}
            </div>
          </div>
          <div
            className="container-r-info-p"
            style={{ marginTop: "15px", marginBottom: "5px" }}
          >
            <div className="label-req-title">Proveedor </div>
            <div className="r-list-item-details-title-v">
              {comprobante.nroCtaDescrp}
            </div>
          </div>
          {comprobante.codDim && comprobante.subCue ? (
            <div
              className="container-r-info-p"
              style={{ marginTop: "15px", marginBottom: "5px" }}
            >
              <div className="label-req-title">Dimensión </div>
              <div className="r-list-item-details-title-v">
                {comprobante.codDim} - {comprobante.codDimDescrp}
              </div>
              <div className="label-req-title">Subcuenta </div>
              <div className="r-list-item-details-title-v">
                {comprobante.subCue} - {comprobante.subCueDescrp}
              </div>
            </div>
          ) : null}
          <div
            className="container-r-info-p"
            style={{ marginTop: "15px", marginBottom: "5px" }}
          >
            <div className="label-req-title">Importe total </div>
            <div className="r-list-item-details-title-v">
              $ {importeTotalString}
            </div>
            <div className="label-req-title">Moneda </div>
            <div className="r-list-item-details-title-v">
              {comprobante.moneda}
            </div>
            {comprobante.cotizacion && (
              <>
                <div className="label-req-title">Cotización </div>
                <div className="r-list-item-details-title-v">
                  $ {cotizacionTotalString}
                </div>
              </>
            )}
          </div>
          <div
            className="container-r-info-p"
            style={{ marginTop: "15px", marginBottom: "5px" }}
          >
            {usuario.userType.some((item) =>
              MODIFICA_GRUPOS_COMPROBANTES.includes(item.typeName)
            ) && (
              <>
                <div className="label-req-title">Grupos: </div>
                <div className="r-list-item-details-title-v">
                  {comprobante.gruposV.map((grupo) => grupo.name).join(" | ")}
                </div>
              </>
            )}
          </div>
        </div>
        {comprobante.motivoObserv !== "" && (
          <>
            <div className="container-r-item-title-details">
              Motivo observación
            </div>
            <div className="container-r-motivo-observ">
              {comprobante.motivoObserv.split("|").map((motivo, index) => (
                <div className="label-req-motivo-observ">
                  <div key={index} className="r-list-item-details-data-v">
                    ► {motivo.trim()}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {comprobante.ocsApl?.length > 0 && (
          <>
            <div className="container-r-item-title-details">
              Órdenes de Compra
            </div>
            <div className="container-r-motivo-observ">
              {comprobante.ocsApl.map((oc, ocIndex) => (
                <div key={ocIndex}>
                  <div className="label-req-motivo-observ">
                    <div className="r-list-item-details-data-v">
                      ▪ <u>Número:</u> {oc.nroFor}
                    </div>
                  </div>

                  {oc.remitos?.length > 0 && (
                    <>
                      <div className="label-req-motivo-observ" style={{marginLeft:"20px"}}>
                        <div className="r-list-item-details-data-v">
                          <u>Remitos asociados:</u>
                        </div>
                      </div>

                      {oc.remitos.map((remito, index) => (
                        <div className="label-req-remito-observ" style={{marginLeft:"20px"}}>
                          <div key={index} className="r-list-item-details-data-v">
                            {remito.codForRem} - {remito.nroForRem}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        <div className="container-r-item-title-details">Info Adicional</div>
        <div className="container-r-texto">
          <div className="r-list-item-details-data-v">{comprobante.observ}</div>
        </div>
        <div className="container-r-item-title-details">Fecha</div>
        <div className="container-r-fecha">
          <div className="label-req-fecha">
            <div className="r-list-item-details-data-v">Alta </div>
            <div className="r-list-item-details-fecha-v">
              {moment(comprobante.fecAlt).format("DD-MM-YYYY")}
            </div>
          </div>
          <div className="label-req-fecha">
            <div className="r-list-item-details-data-v">Emisión </div>
            <div className="r-list-item-details-fecha-v">
              {moment(comprobante.dateOfIssue).format("DD-MM-YYYY")}
            </div>
          </div>
        </div>

        {conceptos.length > 0 && (
          <>
            <div className="container-r-item-title-details">Conceptos</div>
            <div className="container-list-products-r-gral">
              {conceptos.map((concepto, idx) => (
                <div className="container-list-products-r" key={idx} style={{ marginBottom: "12px" }}>
                  <div className="container-list-product-r-icon">
                    <div className="list-item-icon-r">
                      <FontAwesomeIcon icon={faBox} />
                    </div>
                    {concepto.codigoERP && (
                      <div className="list-item-title-r">{concepto.codigoERP.codRed}</div>
                    )}
                  </div>
                  <div className="container-list-products-r-details">
                    <div>{concepto.description}</div>
                    {concepto.codigoERP && (
                      <div style={{ color: "rgb(108,108,108)", fontSize: "0.9rem" }}>
                        {concepto.codigoERP.descrp}
                      </div>
                    )}
                    <div className="container-cantidad-product-r" style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "4px" }}>
                      <span style={{ background: "#f0f0f0", borderRadius: "6px", padding: "3px 10px", fontSize: "1rem" }}>
                        Cantidad: <strong>{concepto.quantity}</strong>
                      </span>
                      <span style={{ background: "#f0f0f0", borderRadius: "6px", padding: "3px 10px", fontSize: "1rem" }}>
                        Precio Unitario: <strong>{formatter.format(concepto.unitPrice ? concepto.unitPrice : 0)}</strong>
                      </span>
                      <span style={{ background: "#e8edf8", borderRadius: "6px", padding: "3px 10px", fontSize: "1rem", color: "#3d4d87" }}>
                        Total: <strong>{formatter.format(concepto.amount ? concepto.amount : 0)}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="container-r-item-title-details">
          <div>Nota</div>
          {!editNota && (
            <div className="container-button-download">
              {!editNotaReservada && (
                <button className="button-edit" onClick={handleEditNota}>
                  <FontAwesomeIcon icon={faPen} />{" "}
                </button>
              )}
            </div>
          )}
          {editNota && (
            <div className="container-button-download">
              <button className="button-edit" onClick={handleEditNota}>
                <FontAwesomeIcon icon={faRectangleXmark} />
              </button>
              <button className="button-edit" onClick={saveChangesNota}>
                <FontAwesomeIcon icon={faFloppyDisk} />
              </button>
            </div>
          )}
        </div>
        <div className="container-r-texto">
          {!editNota && (
            <div className="r-list-item-details-data-v">
              {comprobante.notePubl}
            </div>
          )}
          {editNota && (
            <textarea
              className="r-textarea-input"
              value={comprobante.notePubl}
              onChange={(data) => handleModificaNota(data.target.value)}
            />
          )}
        </div>
        {usuario.userType.some((item) =>
          EDITA_Y_VISUALIZA_NOTAS_PRIVADAS_COMPROBANTE.includes(item.typeName)
        ) && (
          <>
            <div className="container-r-item-title-details">
              <div>Nota Reservada</div>
              {!editNotaReservada && (
                <div className="container-button-download">
                  {!editNota && (
                    <button
                      className="button-edit"
                      onClick={handleEditNotaReservada}
                    >
                      <FontAwesomeIcon icon={faPen} />{" "}
                    </button>
                  )}
                </div>
              )}
              {editNotaReservada && (
                <div className="container-button-download">
                  <button
                    className="button-edit"
                    onClick={handleEditNotaReservada}
                  >
                    <FontAwesomeIcon icon={faRectangleXmark} />
                  </button>
                  <button
                    className="button-edit"
                    onClick={saveChangesNotaReservada}
                  >
                    <FontAwesomeIcon icon={faFloppyDisk} />
                  </button>
                </div>
              )}
            </div>
            <div className="container-r-texto">
              {!editNotaReservada && (
                <div className="r-list-item-details-data-v">
                  {comprobante.notePriv}
                </div>
              )}
              {editNotaReservada && (
                <textarea
                  className="r-textarea-input"
                  value={comprobante.notePriv}
                  onChange={(data) =>
                    handleModificaNotaReservada(data.target.value)
                  }
                />
              )}
            </div>
          </>
        )}
        <div className="container-r-item-title-details">
          <div>Archivos</div>
          <div className="container-button-download">
            <button className="button-download" onClick={handleDownloadAll}>
              Descargar Todos <FontAwesomeIcon icon={faDownload} />{" "}
            </button>
          </div>
        </div>
        <div className="container-files-rq">
          <ul className="container-files-rq-details">
            {files.map((file) => (
              <li
                className="container-files-rq-details-items"
                key={file.name}
                onClick={() => abrirURL(file.url)}
              >
                {file.name}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="rq-container-buttons-bottom">
        <div
          className="rq-button-bottom"
          onClick={() => generaPDF(comprobante, "Admin")}
        >
          <FontAwesomeIcon icon={faFilePdf} /> PDF
        </div>
        <div
          className="rq-button-bottom"
          onClick={() => repetir(comprobante, products)}
        >
          <FontAwesomeIcon icon={faRotateRight} /> Repetir
        </div>
      </div>
    </div>
  );
}

export default ReqInfo;